import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faEdit,
  faEye,
  faHome,
  faInfoCircle,
  faStethoscope,
  faUsers,
  faQuestionCircle,
  faBriefcase,
  faNewspaper,
  faDollarSign,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import { contentService } from "../../lib/supabase";

const ContentManager = () => {
  const [selectedPage, setSelectedPage] = useState("home");
  const [contentSections, setContentSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [message, setMessage] = useState("");

  const pages = [
    { id: "home", name: "Pagina Principală", icon: faHome },
    { id: "about", name: "Despre Noi", icon: faInfoCircle },
    { id: "services", name: "Servicii", icon: faStethoscope },
    { id: "team", name: "Echipa", icon: faUsers },
    { id: "faq", name: "FAQ", icon: faQuestionCircle },
    { id: "careers", name: "Cariere", icon: faBriefcase },
    { id: "articles", name: "Articole", icon: faNewspaper },
    { id: "prices", name: "Prețuri", icon: faDollarSign },
    { id: "bookings", name: "Programări", icon: faCalendarCheck },
    { id: "contact", name: "Contact", icon: faInfoCircle },
  ];

  // Content sections organized by page
  const contentStructure = {
    home: [
      { key: "header_title", label: "Titlu Principal", type: "text" },
      { key: "header_subtitle", label: "Subtitlu Principal", type: "textarea" },
      { key: "header_button_1", label: "Buton 1 - Text", type: "text" },
      { key: "header_button_2", label: "Buton 2 - Text", type: "text" },
      {
        key: "about_section_title",
        label: "Titlu Secțiune Despre",
        type: "text",
      },
      {
        key: "about_main_title",
        label: "Titlu Principal Despre",
        type: "text",
      },
      {
        key: "about_description_1",
        label: "Descriere Despre 1",
        type: "textarea",
      },
      {
        key: "about_description_2",
        label: "Descriere Despre 2",
        type: "textarea",
      },
      {
        key: "services_section_title",
        label: "Titlu Secțiune Servicii",
        type: "text",
      },
      { key: "services_cta_button", label: "Buton CTA Servicii", type: "text" },
      {
        key: "team_section_title",
        label: "Titlu Secțiune Echipa",
        type: "text",
      },
      {
        key: "testimonials_section_title",
        label: "Titlu Secțiune Testimoniale",
        type: "text",
      },
      {
        key: "locations_section_title",
        label: "Titlu Secțiune Locații",
        type: "text",
      },
    ],
    about: [
      { key: "about_hero_title", label: "Titlu Hero Despre", type: "text" },
      {
        key: "about_hero_subtitle",
        label: "Subtitlu Hero Despre",
        type: "textarea",
      },
      { key: "about_mission_title", label: "Titlu Misiune", type: "text" },
      {
        key: "about_mission_content",
        label: "Conținut Misiune",
        type: "textarea",
      },
    ],
    services: [
      {
        key: "services_hero_title",
        label: "Titlu Hero Servicii",
        type: "text",
      },
      {
        key: "services_hero_subtitle",
        label: "Subtitlu Hero Servicii",
        type: "textarea",
      },
    ],
    team: [
      { key: "team_hero_title", label: "Titlu Hero Echipa", type: "text" },
      {
        key: "team_hero_subtitle",
        label: "Subtitlu Hero Echipa",
        type: "textarea",
      },
    ],
    contact: [
      { key: "contact_phone_1", label: "Telefon 1", type: "text" },
      { key: "contact_phone_2", label: "Telefon 2", type: "text" },
      { key: "contact_email", label: "Email", type: "text" },
      { key: "contact_address", label: "Adresă", type: "textarea" },
      { key: "contact_hours", label: "Program Lucru", type: "text" },
    ],
    faq: [
      { key: "faq_hero_title", label: "Titlu Hero FAQ", type: "text" },
      {
        key: "faq_hero_subtitle",
        label: "Subtitlu Hero FAQ",
        type: "textarea",
      },
    ],
    careers: [
      { key: "careers_hero_title", label: "Titlu Hero Cariere", type: "text" },
      {
        key: "careers_hero_subtitle",
        label: "Subtitlu Hero Cariere",
        type: "textarea",
      },
    ],
    articles: [
      {
        key: "articles_hero_title",
        label: "Titlu Hero Articole",
        type: "text",
      },
      {
        key: "articles_hero_subtitle",
        label: "Subtitlu Hero Articole",
        type: "textarea",
      },
    ],
    prices: [
      { key: "prices_hero_title", label: "Titlu Hero Prețuri", type: "text" },
      {
        key: "prices_hero_subtitle",
        label: "Subtitlu Hero Prețuri",
        type: "textarea",
      },
    ],
    bookings: [
      {
        key: "bookings_hero_title",
        label: "Titlu Hero Programări",
        type: "text",
      },
      {
        key: "bookings_hero_subtitle",
        label: "Subtitlu Hero Programări",
        type: "textarea",
      },
    ],
  };

  useEffect(() => {
    loadContentForPage(selectedPage);
  }, [selectedPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadContentForPage = async (page) => {
    setLoading(true);
    try {
      const sections = contentStructure[page] || [];
      const content = {};

      for (const section of sections) {
        const data = await contentService.getContent(section.key, page);
        content[section.key] = data ? data.content : "";
      }

      setContentSections(content);
    } catch (error) {
      console.error("Error loading content:", error);
      setMessage("Eroare la încărcarea conținutului");
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (sectionKey, value) => {
    setContentSections((prev) => ({
      ...prev,
      [sectionKey]: value,
    }));
  };

  const handleSaveSection = async (sectionKey) => {
    setSaving(true);
    try {
      await contentService.upsertContent(
        sectionKey,
        contentSections[sectionKey],
        selectedPage
      );
      setMessage("Conținutul a fost salvat cu succes!");
      setEditingSection(null);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving content:", error);
      setMessage("Eroare la salvarea conținutului");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const sections = contentStructure[selectedPage] || [];

      for (const section of sections) {
        if (contentSections[section.key] !== undefined) {
          await contentService.upsertContent(
            section.key,
            contentSections[section.key],
            selectedPage
          );
        }
      }

      setMessage("Tot conținutul a fost salvat cu succes!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving all content:", error);
      setMessage("Eroare la salvarea conținutului");
    } finally {
      setSaving(false);
    }
  };

  const currentPageSections = contentStructure[selectedPage] || [];

  return (
    <div className="content-manager">
      <div className="content-manager-header">
        <h1>Content Manager</h1>
        <p>Editează conținutul textual al site-ului</p>

        {message && (
          <div
            className={`alert ${
              message.includes("Eroare") ? "alert-error" : "alert-success"
            }`}
          >
            {message}
          </div>
        )}
      </div>

      <div className="content-manager-body">
        {/* Page Selector */}
        <div className="page-selector">
          <h3>Selectează Pagina</h3>
          <div className="page-tabs">
            {pages.map((page) => (
              <button
                key={page.id}
                className={`page-tab ${
                  selectedPage === page.id ? "active" : ""
                }`}
                onClick={() => setSelectedPage(page.id)}
              >
                <FontAwesomeIcon icon={page.icon} />
                <span>{page.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        <div className="content-sections">
          <div className="sections-header">
            <h3>
              Conținut pentru: {pages.find((p) => p.id === selectedPage)?.name}
            </h3>
            <button
              className="btn btn-primary"
              onClick={handleSaveAll}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} />
              {saving ? "Se salvează..." : "Salvează Tot"}
            </button>
          </div>

          {loading ? (
            <div className="loading">Încărcare conținut...</div>
          ) : (
            <div className="sections-grid">
              {currentPageSections.map((section) => (
                <div key={section.key} className="content-section-card">
                  <div className="card-header">
                    <h4>{section.label}</h4>
                    <div className="card-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() =>
                          setEditingSection(
                            editingSection === section.key ? null : section.key
                          )
                        }
                      >
                        <FontAwesomeIcon
                          icon={editingSection === section.key ? faEye : faEdit}
                        />
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleSaveSection(section.key)}
                        disabled={saving}
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                    </div>
                  </div>

                  <div className="card-content">
                    {editingSection === section.key ? (
                      section.type === "textarea" ? (
                        <textarea
                          value={contentSections[section.key] || ""}
                          onChange={(e) =>
                            handleContentChange(section.key, e.target.value)
                          }
                          rows={4}
                          className="form-control"
                          placeholder={`Editează ${section.label}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={contentSections[section.key] || ""}
                          onChange={(e) =>
                            handleContentChange(section.key, e.target.value)
                          }
                          className="form-control"
                          placeholder={`Editează ${section.label}`}
                        />
                      )
                    ) : (
                      <div className="content-preview">
                        {contentSections[section.key] || <em>Conținut gol</em>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentManager;
